// Phase SC-G1 GAP 3 — Empty seed state panel.
//
// AC-3.1 / AC-3.2 — replaces the 4 financial metric cards with a single
// promotion panel + CTA. AC-3.3 is enforced by the page itself: the
// pick card stays visible, only the financial strip swaps.

import type { ScReserve } from "../../singles/useSinglesReserve.ts";

export interface EmptySeedPanelProps {
  /** Click handler — opens the Set Seed Stake modal. */
  onSetSeed: () => void;
}

/**
 * Pure detection helper. Unseeded = no capital and no profit yet.
 *
 * The brief's `seed_declared` boolean does not exist in `sc_reserves`,
 * so we derive from balances. A user with a deleted seed who later
 * funds the chain would re-trigger this state, which matches the
 * empty-state behaviour the brief specifies.
 */
export function isReserveUnseeded(
  reserve: Pick<ScReserve, "capital_balance" | "profit_balance"> | null,
): boolean {
  if (!reserve) return true;
  return reserve.capital_balance === 0 && reserve.profit_balance === 0;
}

const PANEL_STYLE: React.CSSProperties = {
  gridColumn: "1 / -1",
  padding: 14,
  borderRadius: 10,
  border: "1px solid var(--border)",
  background: "var(--bg-elev)",
  display: "flex",
  flexDirection: "column",
  alignItems: "flex-start",
  gap: 12,
};

const CTA_STYLE: React.CSSProperties = {
  padding: "10px 18px",
  borderRadius: 6,
  border: "1px solid var(--fp-accent)",
  background: "var(--fp-accent-soft)",
  color: "var(--fp-accent-text)",
  fontFamily: "var(--font-mono)",
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: "0.12em",
  textTransform: "uppercase" as const,
  cursor: "pointer",
};

export function EmptySeedPanel({
  onSetSeed,
}: EmptySeedPanelProps): JSX.Element {
  return (
    <div
      className="sc-empty-seed"
      style={PANEL_STYLE}
      data-testid="sc-empty-seed-panel"
    >
      <div
        style={{
          fontFamily: "'Bebas Neue', sans-serif",
          fontSize: 22,
          letterSpacing: "0.01em",
          color: "var(--text)",
        }}
      >
        Set your opening stake to start your chain
      </div>
      <div
        style={{
          fontSize: 12.5,
          color: "var(--text-secondary)",
          maxWidth: 560,
          lineHeight: 1.45,
        }}
      >
        Your seed is the foundation of every cycle. Set it once and the
        chain compounds from there — profit only rolls forward, your
        capital is protected from Day 2 onwards.
      </div>
      <button
        type="button"
        onClick={onSetSeed}
        style={CTA_STYLE}
        data-testid="sc-set-seed-cta"
      >
        Set Seed Stake
      </button>
    </div>
  );
}
